import { UserProfile } from "@shared/schema";
import { FriendSearch } from "@/components/ui/friend-search";
import { FriendList } from "@/components/ui/friend-list";
import { FriendInvites } from "@/components/ui/friend-invites";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  UserPlus,
  BellRing,
  UsersRound,
  UserCheck,
  Users,
  UserRound,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PullToRefresh from "react-simple-pull-to-refresh";
import { EnhancedHeader } from "@/components/ui/enhanced-header";
import { apiRequest } from "@/lib/queryClient";
import { useStableSocket } from "@/hooks/use-stable-socket";

// Assuming this type exists, otherwise adjust accordingly
type Friend = {
  id: string;
  username: string;
  status: "pending" | "accepted" | "rejected";
  // ... other properties
};

export default function FriendsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const queryClient = useQueryClient();
  const [_, navigate] = useLocation();
  const { user } = useAuth();
  
  // Initialize WebSocket connection
  const { isConnected, sendMessage } = useStableSocket({
    autoAuth: true,
    debug: true
  });

  // Fetch friends list to count pending invites
  const { data: friendsList } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/friends");
      return res.json();
    },
    enabled: !!user
  });

  // Count pending invites
  const pendingInvites = friendsList?.filter(
    (invite: any) => invite.status === "pending"
  )?.length || 0;

  // Set page title
  useEffect(() => {
    document.title = "Friends - Spar";

    return () => {
      document.title = "Spar";
    };
  }, []);

  // Animation variants
  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  const handleRefresh = async () => {
    // Invalidate friends list and related queries
    await queryClient.invalidateQueries({ queryKey: ["friends"] });
    await queryClient.invalidateQueries({ queryKey: ["friend-suggestions"] });
    await queryClient.invalidateQueries({ queryKey: ["friend-requests"] });
    
    // If we're connected, refresh online status
    if (isConnected && user) {
      const acceptedFriends = friendsList?.filter((friend: any) => friend.status === "accepted") || [];
      const friendUserIds = acceptedFriends.map((friend: any) => 
        friend.userId === user.id ? friend.friendId : friend.userId
      );
      
      if (friendUserIds.length > 0) {
        sendMessage({
          type: "GET_ONLINE_STATUS",
          userIds: friendUserIds
        });
      }
    }
    
    return Promise.resolve();
  };

  // Listen for successful authentication
  useEffect(() => {
    if (isConnected && user) {
      // Request online status for all accepted friends
      const acceptedFriends = friendsList?.filter((friend: any) => friend.status === "accepted") || [];
      const friendUserIds = acceptedFriends.map((friend: any) => 
        friend.userId === user.id ? friend.friendId : friend.userId
      );
      
      if (friendUserIds.length > 0) {
        sendMessage({
          type: "GET_ONLINE_STATUS",
          userIds: friendUserIds
        });
      }
    }
  }, [isConnected, user, friendsList, sendMessage]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-black/95 to-black/90 flex flex-col items-center justify-center py-12 text-white/60">
        <Users className="h-12 w-12 opacity-20 mb-4" />
        <p>Please log in to view your friends</p>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-b from-black via-black/95 to-black/90 text-white pb-16">
        {/* Enhanced Header to match profile page */}
        <EnhancedHeader 
          title="Friends" 
          showBackButton={false}
          backPath="/"
          coloredText={true}
          accentColor="text-primary"
          rightContent={
            <div className="flex gap-1">
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full bg-black/60 hover:bg-black/40 text-white w-8 h-8"
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-black/90 border-white/20 backdrop-blur-md">
                  <DialogHeader>
                    <DialogTitle className="text-white text-center">
                      Add New Friend
                    </DialogTitle>
                  </DialogHeader>
                  <div className="p-4 space-y-4">
                    <FriendSearch />
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full relative bg-black/60 hover:bg-black/40 text-white w-8 h-8"
                  >
                    <BellRing className="h-4 w-4" />
                    {pendingInvites > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                        {pendingInvites}
                      </span>
                    )}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-black/90 border-white/20 backdrop-blur-md">
                  <DialogHeader>
                    <DialogTitle className="text-white text-center">
                      Friend Requests
                    </DialogTitle>
                  </DialogHeader>
                  <div className="p-4 space-y-4">
                    <FriendInvites searchQuery={searchQuery} />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          }
        />
      
      <PullToRefresh 
        onRefresh={handleRefresh}
        pullingContent={
          <div className="text-center p-2">
            <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
          </div>
        }
        refreshingContent={
          <div className="text-center p-4">
            <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="text-sm text-white/60 mt-1">Refreshing friends list...</p>
          </div>
        }
      >
        {/* Main content */}
        <div className="container max-w-6xl mx-auto px-4 pt-2 min-h-[calc(100vh-60px)]">
          <div className="w-full">
            {/* Search and filter */}
            <div className="relative z-10 bg-black/60 backdrop-blur-md rounded-lg  border-white/10 mb-4 ">
              <div className="flex flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    placeholder="Search friends..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 py-2 h-9 bg-black/40 border-white/10 focus:border-primary/50 rounded-lg w-full"
                  />
                </div>

                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[130px] h-9 bg-black/40 border-white/10 focus:border-primary/50 rounded-lg">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent className="bg-black/90 border-white/20 text-white">
                    <SelectItem value="all">All Friends</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key="friends-list"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={pageVariants}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                  <CardHeader className="border-b border-white/5 bg-black/30 px-4 py-3">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium">
                      <UsersRound className="h-4 w-4 text-primary" />
                      <span>Your Friends</span>
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-0">
                    <div className="p-4">
                      <FriendList searchQuery={searchQuery} filter={filter} />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        </PullToRefresh>

      </div>
  );
}
